import React, {ReactElement, useMemo, useState} from "react";
import Table from "react-bootstrap/Table";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGear} from "@fortawesome/pro-regular-svg-icons";
import {Card, Modal} from "react-bootstrap";
import {Pagination, TableColumns} from "@src/components";
import {
    getProcessColumn,
    getAvailableColumns,
} from "@process/components/ProcessTable/ProcessTable.column";
import {ProcessTableProps} from "@process/components/ProcessTable/ProcessTable.types";
import {ProcessDropdownOptions} from "@process/components";
import {faTableColumns} from "@fortawesome/pro-light-svg-icons";
import {Link} from "react-router-dom";

const ProcessTable: React.FC<ProcessTableProps> = (
    {processCollection, processFilter, pagination}
): ReactElement => {
    const tableColumns = useMemo(() => getProcessColumn(processFilter), [processFilter]);
    const availableColumns = useMemo(() => getAvailableColumns(processFilter), [processFilter]);
    const [showProcessColumnsModal, setShowProcessColumnsModal] = useState<boolean>(false);

    const columnVisibility = availableColumns.reduce((acc, column) => {
        acc[column.id] = column.visible;
        return acc;
    }, {})

    const table = useReactTable({
        data: processCollection,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnVisibility: columnVisibility,
            columnOrder: processFilter.column,
        }
    })

    return (
        <>
            <Card>
                <Table responsive hover bordered className="table-vcenter card-table card-table-options">
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id} scope="col" {...header.column.columnDef.meta}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                </th>
                            ))}
                            <th className="text-center" style={{width: 0}}>
                                <span onClick={() => setShowProcessColumnsModal(true)}
                                      className="position-relative cursor-pointer"
                                >
                                    <FontAwesomeIcon icon={faGear}/>
                                    <span className="badge bg-yellow badge-notification badge-blink"></span>
                                </span>
                            </th>
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} {...cell.column.columnDef.meta}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                            <td className="text-center">
                                <ProcessDropdownOptions process={row.original} buttonVariant="outline-cyan"/>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                {pagination && (
                    <Card.Footer>
                        <Pagination pagination={pagination} searchParams={processFilter} />
                    </Card.Footer>
                )}
            </Card>

            <Modal show={showProcessColumnsModal} onHide={() => setShowProcessColumnsModal(false)}
                   size={"lg"} centered={true}
                   className="modal-blur"
            >
                <Modal.Header closeButton>
                    <Modal.Title as="h5"><FontAwesomeIcon icon={faTableColumns} fixedWidth={true}/> Wybierz kolumny tabelki</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TableColumns
                        availableColumns={availableColumns}
                        onColumnSelected={() => setShowProcessColumnsModal(false)}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ProcessTable;